import CollabFeatureIcon from 'assets/icons/mono/CollabFeatureIcon';
import CompetitorTrackingIcon from 'assets/icons/mono/CompetitorTrackingIcon';
import MarketplaceFeeIcon from 'assets/icons/mono/MarketplaceFeeIcon';
import Loader from 'Components/Common/Loader';
import { ANALYTICS_EVENTS } from 'constants/analyticsEvents';
import { RAZORPAY_SDK } from 'constants/global';
import ifConfigured from 'hoc/ifConfigured';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify';
import { analyticsLog } from 'services/analytics';
import { changeMembership, startMembership, verifyMembershipPayment } from 'services/api/membership';
import { fetchMembershipThunk, fetchSubscriptionPlansThunk } from 'slices/membership/thunk';
import { AppDispatch, RootState } from 'store';
import { SubscriptionFrequencies } from 'types/membership';
import { formatAmount, getErrorMessage, loadScript } from 'utils/helpers';

const SubscriptionCheckout = () => {

    useDocumentTitle("Checkout - Subscription Plans")
    const [searchParams] = useSearchParams();
    const payload = searchParams.get('payload');
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const [planId, setPlanId] = useState<string | null>(null);
    const [frequency, setFrequency] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);
    const selectedAgencyId = useSelector((state:RootState) => state.auth.selectedAgencyId)

    const { membership: membershipData, subscription_plans: plansData } = useSelector((state: RootState) => state.membershipState);

    const activePlan = useMemo(() => {
        return membershipData?.data?.active_plan;
    }, [membershipData])

    const plan = useMemo(() => {
        return (plansData?.data?.plans ?? []).find((plan) => Number(plan.id) === Number(planId));
    }, [planId, plansData])

    const planFrequency = useMemo(() => {
        if (!frequency || !plan?.frequencies) return null;
        return plan.frequencies[frequency] ?? null;
    }, [frequency, plan])

    const featuresList = useSelector((state: RootState) => state.membershipState.subscription_plans.data?.features)

    const features = useMemo(() => {
        if (!plan || !featuresList) return {};
        return (featuresList ?? [])?.filter((feature) => feature.is_visible).reduce((acc, feature) => {
            acc[feature.slug] = { ...feature, value: plan.features[feature.id]?.value };
            return acc;
        }, {} as Record<string, any>)
    }, [featuresList, plan])


    useEffect(() => {
        if (payload) {
            setLoading(true)
            const decodedData = atob(payload);
            const data = JSON.parse(decodedData);
            if (data?.planId && data?.frequency) {
                setPlanId(data.planId);
                setFrequency(data.frequency);
                setLoading(false);
            } else {
                navigate('/subscription-plans');
            }
        } else {
            navigate('/subscription-plans');
        }
    }, [payload])

    useEffect(() => {
        dispatch(fetchMembershipThunk({}));
        dispatch(fetchSubscriptionPlansThunk({}));
    }, [])

    const taxInfo = useMemo(() => {
        if (!planFrequency) return { percentage: 0, amount: 0 };
        const tax = Number(planFrequency.amount) - Number(planFrequency.pre_tax_amount);
        return { amount: tax, percentage: (tax / Number(planFrequency.pre_tax_amount)) * 100 };
    }, [planFrequency])

    const handleCheckout = async () => {
        const freq = frequency;
        const allowStart = plansData?.data?.allow_start ?? false;
        const activePlanId = activePlan?.uuid;

        if (!plan || !freq || (!allowStart && !activePlanId) || !selectedAgencyId) return;

        setProcessing(true);
        analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_START, {
            plan: plan.name,
            frequency: freq,
        })
        try {
            const checkoutData = allowStart
                ? await startMembership(selectedAgencyId, (freq as SubscriptionFrequencies), plan.id)
                : await changeMembership(selectedAgencyId, (freq as SubscriptionFrequencies), plan.id, activePlanId!);


            const loaded = await loadScript(RAZORPAY_SDK);
            if (!loaded) throw new Error("Razorpay SDK failed");

            const options = {
                key: checkoutData.checkout.key,
                description: checkoutData.checkout.plan_name,
                handler: async (res: any) => {
                    await verifyMembershipPayment(
                        checkoutData.ump.uuid,
                        res.razorpay_payment_id,
                        res.razorpay_signature
                    );
                    analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_SUCCESS, { plan_name: plan?.name, frequency: freq })
                    await dispatch(fetchMembershipThunk({ force: true }));
                    await dispatch(fetchSubscriptionPlansThunk({ force: true }));
                    navigate("/subscription-plans");
                    setProcessing(false);
                },
                modal: {
                    ondismiss: () => {
                        const error = "Payment cancelled";
                        toast.warn(error);
                        analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_FAILED, {
                            plan: plan?.name,
                            frequency: freq,
                            error
                        })
                        setProcessing(false);
                    },
                },
                ...(checkoutData.ump.platform === "rzp_checkout"
                    ? { order_id: checkoutData.ump.platform_reference.razorpay_order_id }
                    : {
                        subscription_id:
                            checkoutData.ump.platform_reference
                                .razorpay_subscription_id,
                    }),
            };

            new (window as any).Razorpay(options).open();
        } catch (err) {
            analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_FAILED, {
                plan: plan?.name,
                frequency: freq,
                error: getErrorMessage(err)
            })
            // console.log("iap_error", err);
            toast.error(getErrorMessage(err));
            setProcessing(false);

            if (typeof err === "string") {
                // debug({ message: err });
            } else if (err instanceof Error) {
                // debug({ message: err.message, stack: err.stack });
            }
        }
    }

    if (loading || (!planId || !frequency)) return <Loader />

    return (
        <div className="campaign-page">
            <h1 className="campaign-page-title d-flex align-items-center gap-2">
                <Link to="/subscription-plans" className='text-dark'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M7.33073 16.0312L24.6641 16.0312" stroke="#222222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M15.3984 24.0653L7.33177 16.0333L15.3984 8" stroke="#222222" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </Link>
                Review & Pay
            </h1>
            <div className="py-3 px-4 mx-2">
                <div className="row g-4">
                    <div className="col-md-7">
                        <div className="checkout-items-card">
                            <div className="checkout-items-card-header">
                                <h3 className="checkout-items-card-title m-0">Upgrade plan</h3>
                            </div>
                            <div className="checkout-items-card-body">
                                <div className="d-flex justify-content-between gap-2">
                                    <div className="d-flex flex-column gap-2">
                                        <div className="d-flex flex-column">
                                            <span className="checkout-plan-name">
                                                {plan?.name} Plan
                                            </span>
                                            <span className="checkout-plan-amount">
                                                {formatAmount(planFrequency?.pre_tax_amount ?? 0, planFrequency?.currency?.code, planFrequency?.currency?.symbol, planFrequency?.currency?.locale)}/{frequency === 'monthly' ? 'month' : (frequency === 'yearly' ? 'year' : '----')}
                                            </span>
                                        </div>
                                        <div className="checkout-plan-features">
                                            {
                                                features.collabs &&
                                                <div className='d-flex align-items-center justify-content-between gap-5 w-100'>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <CollabFeatureIcon size={20} />
                                                        <span className='checkout-plan-feature-name'>{features.collabs.display_title}</span>
                                                    </div>
                                                    <span className='checkout-plan-feature-value'> <strong>{features.collabs.value}</strong> {features.collabs.unit_title}{(Number(features.collabs.value ?? 0)) > 1 ? 's' : ''}</span>
                                                </div>
                                            }
                                            {
                                                features.max_competitors &&
                                                <div className='d-flex align-items-center justify-content-between gap-5 w-100'>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <CompetitorTrackingIcon size={20} />
                                                        <span className='checkout-plan-feature-name'>{features.max_competitors.display_title}</span>
                                                    </div>
                                                    <span className='checkout-plan-feature-value'> <strong>{features.max_competitors.value}</strong> {features.max_competitors.unit_title}{Number(features.max_competitors.value ?? 0) > 1 ? 's' : ''}</span>
                                                </div>
                                            }
                                            {
                                                features.max_ig_accounts &&
                                                <div className='d-flex align-items-center justify-content-between gap-5 w-100'>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <CompetitorTrackingIcon size={20} />
                                                        <span className='checkout-plan-feature-name'>{features.max_ig_accounts.display_title}</span>
                                                    </div>
                                                    <span className='checkout-plan-feature-value'> <strong>{features.max_ig_accounts.value}</strong> {features.max_ig_accounts.unit_title}{Number(features.max_ig_accounts.value ?? 0) > 1 ? 's' : ''}</span>
                                                </div>
                                            }
                                            {
                                                features.commission_rule_percent &&
                                                <div className='d-flex align-items-center justify-content-between gap-5 w-100'>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <MarketplaceFeeIcon size={20} />
                                                        <span className='checkout-plan-feature-name'>{features.commission_rule_percent.display_title}</span>
                                                    </div>
                                                    <span className='checkout-plan-feature-value'> <strong>{features.commission_rule_percent.value}%</strong></span>
                                                </div>
                                            }
                                        </div>

                                    </div>
                                    {/* <button className='unset-all cursor-pointer'>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33" fill="none">
                                            <rect width="33" height="33" rx="6" fill="#FF6262" />
                                            <rect x="8.5" y="8" width="17" height="17" fill="white" />
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M16.5 33C8.72183 33 4.83274 33 2.41637 30.5836C0 28.1673 0 24.2782 0 16.5C0 8.72183 0 4.83274 2.41637 2.41637C4.83274 0 8.72183 0 16.5 0C24.2782 0 28.1673 0 30.5836 2.41637C33 4.83274 33 8.72183 33 16.5C33 24.2782 33 28.1673 30.5836 30.5836C28.1673 33 24.2782 33 16.5 33ZM11.4999 11.5C11.9832 11.0167 12.7667 11.0167 13.25 11.5L16.5 14.7499L19.7499 11.5C20.2332 11.0167 21.0167 11.0167 21.5 11.5C21.9833 11.9833 21.9833 12.7668 21.5 13.2501L18.2501 16.5L21.5 19.7499C21.9832 20.2332 21.9832 21.0167 21.5 21.5C21.0167 21.9833 20.2332 21.9833 19.7499 21.5L16.5 18.2501L13.25 21.5C12.7668 21.9833 11.9832 21.9833 11.5 21.5C11.0167 21.0168 11.0167 20.2332 11.5 19.75L14.7499 16.5L11.4999 13.25C11.0166 12.7668 11.0166 11.9832 11.4999 11.5Z" fill="#FF6262" />
                                        </svg>
                                    </button> */}
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="col-md-5">
                        <div className="order-summary-card">
                            <span className="order-summary-title">Order Summary</span>
                            <div className="order-summary-body w-100">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className='order-summary-item-name'>{plan?.name} Plan</span>
                                    <span className='order-summary-item-value'>{formatAmount(planFrequency?.pre_tax_amount ?? 0, planFrequency?.currency?.code, planFrequency?.currency?.symbol, planFrequency?.currency?.locale)}</span>
                                </div>
                                <div className="divider my-3"></div>
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className='order-summary-item-name'>GST ({taxInfo.percentage}%)</span>
                                    <span className='order-summary-item-value'>{formatAmount(taxInfo.amount, planFrequency?.currency?.code, planFrequency?.currency?.symbol, planFrequency?.currency?.locale)}</span>
                                </div>
                                <div className="divider my-3"></div>
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <span className='order-summary-item-name'>Total due today</span>
                                    <span className='order-summary-item-value'>{formatAmount(planFrequency?.amount ?? 0, planFrequency?.currency?.code, planFrequency?.currency?.symbol, planFrequency?.currency?.locale)}</span>
                                </div>
                            </div>
                            <button className='btn btn-gradient w-100' onClick={handleCheckout}>PAY {formatAmount(planFrequency?.amount ?? 0, planFrequency?.currency?.code, planFrequency?.currency?.symbol, planFrequency?.currency?.locale)}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ifConfigured(SubscriptionCheckout, 'agency_membership_subscription', '/')